import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

// Times/days are computed against the IST day; format in IST so the UI matches.
const IST = 'Asia/Kolkata';

function fmtDuration(sec) {
  if (!sec || sec < 0) return '0h 0m';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}
function fmtTime(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return '--'; }
}
function fmtDate(dStr) {
  try {
    const [y, m, d] = dStr.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' });
  } catch (_) { return dStr; }
}
function todayLocal() { return new Date().toLocaleDateString('en-CA'); }

function DeliveryPartnerWorkAnalysis() {
  const [from, setFrom] = useState(todayLocal());
  const [to, setTo] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = from <= to ? from : to;
      const endDate = from <= to ? to : from;
      const res = await adminAPI.getDeliveryWorkAnalysis({ startDate, endDate });
      setData(res.data?.data || null);
    } catch (e) {
      console.error('Failed to load work analysis:', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const q = search.trim().toLowerCase();
  const partners = (data?.partners || []).filter((p) => {
    if (onlyActive && !(p.totalOnlineSeconds > 0 || p.totalOrders > 0)) return false;
    if (!q) return true;
    return (p.name || '').toLowerCase().includes(q) || (p.phone || '').includes(q);
  });
  const summary = data?.summary || {};
  const multiDay = data && !data.isSingleDay;

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Partner', 'Phone', 'Date', 'Online (h m)', 'Online Hours', 'Orders Delivered']];
    (data.partners || []).forEach((p) => {
      (p.perDay || []).forEach((d) => {
        if (d.onlineSeconds === 0 && d.orders === 0) return;
        rows.push([p.name, p.phone, d.date, fmtDuration(d.onlineSeconds), (d.onlineSeconds / 3600).toFixed(2), d.orders]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delivery-work-${data.startDate}${data.startDate !== data.endDate ? '_to_' + data.endDate : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const card = {
    background: 'var(--white, #fff)', borderRadius: 'var(--radius-md, 12px)',
    boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.1))', padding: 'var(--space-lg, 20px)',
    marginBottom: 'var(--space-md, 16px)',
  };
  const dateInput = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--gray-300, #d1d5db)', fontSize: 14 };

  const renderDay = (d) => (
    <div key={d.date} style={{ padding: '12px 0', borderTop: '1px dashed var(--gray-100, #f3f4f6)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 700, color: 'var(--gray-800, #1f2937)' }}>{fmtDate(d.date)}</div>
        <div style={{ display: 'flex', gap: 14, fontSize: 14 }}>
          <span style={{ color: 'var(--primary, #2aaa8a)', fontWeight: 700 }}>🕒 {fmtDuration(d.onlineSeconds)}</span>
          <span style={{ color: 'var(--gray-700, #374151)', fontWeight: 700 }}>📦 {d.orders} order{d.orders === 1 ? '' : 's'}</span>
        </div>
      </div>
      {d.sessions && d.sessions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
          {d.sessions.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
              <span style={{ color: 'var(--success, #22c55e)' }}>●</span>
              <span style={{ color: 'var(--gray-800, #1f2937)' }}>
                {fmtTime(s.start)} → {s.open ? <em style={{ color: 'var(--gray-500,#6b7280)' }}>ongoing</em> : fmtTime(s.end)}
              </span>
              <span style={{ color: 'var(--gray-500, #6b7280)' }}>({fmtDuration(s.durationSeconds)})</span>
            </div>
          ))}
        </div>
      )}
      {d.events && d.events.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {d.events.map((ev, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, fontSize: 12.5,
              background: ev.status === 'online' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)',
              color: ev.status === 'online' ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
            }}>
              {ev.status === 'online' ? '🟢' : '🔴'}
              {ev.carriedOver ? 'Online (carried over)' : `${ev.status === 'online' ? 'Online' : 'Offline'} ${fmtTime(ev.time)}`}
            </span>
          ))}
        </div>
      ) : (
        (!d.sessions || d.sessions.length === 0) && <div style={{ color: 'var(--gray-400, #9ca3af)', fontSize: 13 }}>No online time this day.</div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50, #f7f8fa)' }}>
      <Navbar />
      <div style={{ padding: 'var(--space-xl, 24px)', maxWidth: 1080, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'var(--font-size-3xl, 28px)', fontWeight: 800, marginBottom: 4, color: 'var(--gray-900, #111827)' }}>
          Delivery Partner Work Analysis
        </h1>
        <p style={{ color: 'var(--gray-500, #6b7280)', marginTop: 0, marginBottom: 'var(--space-lg, 20px)' }}>
          Online/offline times, total online hours, and orders delivered per delivery partner (IST). Pick a day or a date range.
        </p>

        {/* Controls */}
        <div style={{ ...card, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--gray-600, #4b5563)', marginBottom: 4 }}>From</label>
            <input type="date" value={from} max={todayLocal()} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--gray-600, #4b5563)', marginBottom: 4 }}>To</label>
            <input type="date" value={to} max={todayLocal()} onChange={(e) => setTo(e.target.value)} style={dateInput} />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--gray-600, #4b5563)', marginBottom: 4 }}>Search partner</label>
            <input type="text" placeholder="Name or phone…" value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ ...dateInput, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--gray-700, #374151)', cursor: 'pointer', paddingBottom: 8 }}>
            <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} /> Only with activity
          </label>
          <button onClick={exportCSV} disabled={!data}
            style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--primary, #2aaa8a)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            ⬇ Export CSV
          </button>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 'var(--space-lg, 20px)' }}>
          {[
            ['Delivery Partners', summary.totalPartners ?? 0, 'var(--gray-900, #111827)'],
            ['Active in Range', summary.partnersWithActivity ?? 0, 'var(--primary, #2aaa8a)'],
            ['Total Online Time', fmtDuration(summary.totalOnlineSeconds), 'var(--gray-900, #111827)'],
            ['Orders Delivered', summary.totalOrders ?? 0, 'var(--gray-900, #111827)'],
          ].map(([label, val, color]) => (
            <div key={label} style={{ ...card, marginBottom: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ ...card, textAlign: 'center', color: 'var(--gray-500, #6b7280)' }}>Loading…</div>
        ) : partners.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: 'var(--gray-500, #6b7280)' }}>No delivery partners match.</div>
        ) : (
          partners.map((p) => {
            const isOpen = !!expanded[p.partnerId];
            const activeDays = (p.perDay || []).filter((d) => d.onlineSeconds > 0 || d.orders > 0 || (d.events && d.events.length));
            return (
              <div key={p.partnerId} style={card}>
                <div onClick={() => setExpanded((prev) => ({ ...prev, [p.partnerId]: !prev[p.partnerId] }))}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span title={p.isCurrentlyOnline ? 'Currently online' : 'Currently offline'}
                      style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: p.isCurrentlyOnline ? 'var(--success, #22c55e)' : 'var(--gray-300, #d1d5db)' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--gray-900, #111827)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.name}{!p.isApproved && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--warning, #d97706)' }}>(unapproved)</span>}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>{p.phone || '—'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>Online</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: p.totalOnlineSeconds > 0 ? 'var(--primary, #2aaa8a)' : 'var(--gray-400, #9ca3af)' }}>{fmtDuration(p.totalOnlineSeconds)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>Orders</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: p.totalOrders > 0 ? 'var(--gray-800, #1f2937)' : 'var(--gray-400, #9ca3af)' }}>{p.totalOrders}</div>
                    </div>
                    <span style={{ color: 'var(--gray-400, #9ca3af)', fontSize: 18 }}>{isOpen ? '▾' : '▸'}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ marginTop: 6 }}>
                    {activeDays.length === 0 ? (
                      <div style={{ color: 'var(--gray-500, #6b7280)', fontSize: 14, paddingTop: 12, borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
                        No online time or orders {multiDay ? 'in this range.' : 'this day.'}
                      </div>
                    ) : (
                      activeDays.map(renderDay)
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        <p style={{ fontSize: 12, color: 'var(--gray-400, #9ca3af)', marginTop: 20 }}>
          Note: online/offline history is recorded from when this feature was deployed onward; earlier dates show no online data. Orders are counted from delivery completion (deliveredAt).
        </p>
      </div>
    </div>
  );
}

export default DeliveryPartnerWorkAnalysis;
